import axios from 'axios';
import { doc, collection, addDoc, query, where, getDocs, orderBy, limit, getDoc, updateDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

class ChatGptService {
  static API_URL = process.env.REACT_APP_OPENAI_API_URL || 'https://api.openai.com/v1/chat/completions';
  static API_KEY = process.env.REACT_APP_OPENAI_API_KEY ? process.env.REACT_APP_OPENAI_API_KEY.replace(/\s+/g, '') : '';
  static USE_MOCK_RESPONSES = false; // Set to false to use real API calls instead of mock responses

  // Default student services (will be overridden by Firestore data if available)
  static STUDENT_SERVICES = {
    academics: {
      description: 'Academic resources and study tools',
      features: ['course materials', 'study guides', 'tutoring services', 'exam prep']
    },
    career: {
      description: 'Career planning and job search tools',
      features: ['resume builder', 'interview prep', 'job listings', 'internship opportunities']
    },
    campus: {
      description: 'Campus life and resources',
      features: ['event calendar', 'campus map', 'housing info', 'dining options']
    },
    technology: {
      description: 'Tech support and digital resources',
      features: ['software access', 'tech tutorials', 'device support', 'printing services']
    },
    achievements: {
      description: 'User contributions and gamification system',
      features: ['contribution tracking', 'points system', 'achievement badges', 'leaderboards']
    },
    qa: {
      description: 'Question and answer platform',
      features: ['ask questions', 'answer community questions', 'upvote helpful answers', 'topic categories']
    },
    materials: {
      description: 'Study materials and resources library',
      features: ['upload materials', 'download resources', 'search by subject', 'rate materials']
    },
    profile: {
      description: 'User profile management',
      features: ['update personal info', 'change password', 'view activity history', 'manage preferences']
    },
    messaging: {
      description: 'User-to-user communication',
      features: ['direct messaging', 'group chats', 'notifications', 'message history']
    }
  };
  
  // Cache for student services fetched from Firestore
  static cachedServices = null;
  static lastFetchTime = 0;
  static CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
  static chatSubscriptions = {}; // Store active chat subscriptions

  // Initialize service state
  static isInitialized = false;
  
  // Initialize the service with environment variables if needed
  static initialize() {
    if (this.isInitialized) return;
    
    // Check if we need to load from environment
    if (!this.API_KEY && process.env.REACT_APP_OPENAI_API_KEY) {
      this.API_KEY = process.env.REACT_APP_OPENAI_API_KEY.replace(/\s+/g, '');
    }
    
    // Log initialization state but don't expose API key
    console.log('ChatGptService initialized. Using mock responses:', this.USE_MOCK_RESPONSES);
    
    this.isInitialized = true;
  }

  // Get chat completion from OpenAI API or use mock response
  static async getResponse(userMessage, userId, context = []) {
    // Make sure service is initialized
    this.initialize();
    
    try {
      // First check if user wants to speak to a human admin or is asking about services
      if (this.isRequestingHumanAdmin(userMessage)) {
        // Determine if this is a service request rather than a direct admin request
        const isServiceRequest = this.isServiceRequest(userMessage);
        
        let chatRequestId;
        try {
          // Create an admin chat request
          chatRequestId = await this.createAdminChatRequest(userId, userMessage, context);
        } catch (adminError) {
          console.error('Error creating admin chat request:', adminError);
          return "I'm having trouble connecting you with an admin. Please try again later or contact support directly at support@student-helper.com";
        }
        
        // Return a response informing the user that their request has been sent to an admin
        if (isServiceRequest) {
          return "I see you're asking about our services like tutoring or project help. I've forwarded your request to our admin team who can provide specific information about these services, pricing, and availability. They'll respond as soon as possible. Feel free to continue the conversation while you wait.";
        } else {
          return "I understand you'd like to speak with a human admin. I've forwarded your request to our admin team, and they'll respond as soon as possible. You can continue chatting here and an admin will join the conversation shortly.";
        }
      }
      
      // Try to fetch the latest services data but don't fail if it doesn't work
      try {
        await this.refreshStudentServices();
      } catch (serviceError) {
        console.error('Error refreshing services, using cached or default:', serviceError);
      }
      
      // Determine which services the query might relate to
      const relevantServices = this.identifyRelevantServices(userMessage);
      
      // If using mock responses, skip the actual API call
      if (this.USE_MOCK_RESPONSES) {
        console.log('Using mock response instead of API call');
        const mockResponse = this.generateMockResponse(userMessage, relevantServices);
        
        // Save mock message to Firestore if possible
        if (userId) {
          try {
            await this.saveMessageToFirestore(userId, userMessage, false);
            await this.saveMessageToFirestore(userId, mockResponse, true);
          } catch (firestoreError) {
            console.error('Error saving mock messages to Firestore:', firestoreError);
            // Continue even if storage fails
          }
        }
        
        return mockResponse;
      }
      
      // API key validation before proceeding
      if (!this.API_KEY) {
        console.error('OpenAI API key is missing or invalid');
        // Fall back to mock response without throwing an error
        return this.generateFailsafeResponse(userMessage);
      }
      
      let modelToUse = "gpt-3.5-turbo";
      let tokensToUse = 500;
      let tempToUse = 0.7;
      
      // Try to get API settings from Firestore
      try {
        const apiSettingsDoc = await getDoc(doc(db, 'settings', 'chatApiSettings'));
        if (apiSettingsDoc.exists()) {
          const settings = apiSettingsDoc.data();
          if (settings.apiKey) this.API_KEY = settings.apiKey;
          // Use other settings from Firestore if needed
          modelToUse = settings.model || modelToUse;
          tokensToUse = settings.maxTokens || tokensToUse;
          tempToUse = settings.temperature || tempToUse;
        }
      } catch (apiSettingsError) {
        console.error('Error retrieving API settings, using defaults:', apiSettingsError);
        // Continue with defaults if there's an error
      }
      
      // Create system message focused on student helper website
      const systemMessage = "You are the Student Helper Assistant for our comprehensive educational platform. Your role is to guide students in using all website features and answer their questions accurately. Our platform includes: (1) Academics section with study materials, guides, tutoring services, and exam preparation; (2) Career planning with resume building, interview prep, job listings, and internship opportunities; (3) Campus resources including events, housing, dining options, and facilities; (4) Technology support for software, tutorials, device help, and printing; (5) Achievements system that tracks user contributions, points, badges, and rankings on leaderboards; (6) Q&A forum where users can ask and answer questions; (7) Materials library for uploading and downloading study resources; (8) Profile management for personal settings; and (9) Messaging system for communication between users. Direct users to specific sections and explain how features work. You can also connect users with human admins for complex requests or service inquiries.";
      
      // Format the conversation for the API
      const messages = [
        { role: "system", content: systemMessage },
        { role: "system", content: `Available services: ${JSON.stringify(this.cachedServices || this.STUDENT_SERVICES)}` },
        ...context.map(msg => ({
          role: msg.isBot === undefined ? "system" : msg.isBot ? "assistant" : "user",
          content: msg.text || msg.content
        })),
        { role: "user", content: userMessage }
      ];

      // Make request to OpenAI with proper error handling
      try {
        const response = await axios.post(
          this.API_URL,
          {
            model: modelToUse,
            messages: messages,
            temperature: tempToUse,
            max_tokens: tokensToUse
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.API_KEY}`
            },
            timeout: 15000 // 15 second timeout
          }
        );

        // Validate the response structure
        if (!response.data || !response.data.choices || response.data.choices.length === 0 || !response.data.choices[0].message) {
          console.error('Invalid response from OpenAI API:', response.data);
          return this.generateFailsafeResponse(userMessage);
        }

        const aiMessage = response.data.choices[0].message.content;

        // Save message to Firestore if possible
        if (userId) {
          try {
            await this.saveMessageToFirestore(userId, userMessage, false);
            await this.saveMessageToFirestore(userId, aiMessage, true);
          } catch (dbError) {
            console.error('Error saving messages to Firestore:', dbError);
            // Continue even if storage fails
          }
        }

        return aiMessage;
      } catch (apiError) {
        console.error('Error with OpenAI API request:', apiError);
        return this.generateFailsafeResponse(userMessage);
      }
      
    } catch (error) {
      console.error('Unhandled error in ChatGPT service:', error);
      
      // Always have a fallback response
      return this.generateFailsafeResponse(userMessage);
    }
  }
  
  // Failsafe response when all else fails
  static generateFailsafeResponse(userMessage) {
    try {
      const mockResponse = this.generateMockResponse(userMessage, 
        this.identifyRelevantServices(userMessage)
      );
      return mockResponse;
    } catch (e) {
      // Ultimate fallback if even the mock generator fails
      return "I'm sorry, I'm having trouble processing your request right now. Please try again in a few moments or contact support if the issue persists.";
    }
  }

  // Fetch the most up-to-date student services from Firestore
  static async refreshStudentServices() {
    const now = Date.now();
    // Check if cache is still valid
    if (this.cachedServices && (now - this.lastFetchTime < this.CACHE_TTL)) {
      return this.cachedServices;
    }
    
    try {
      // Try to get services from Firestore
      const servicesDoc = await getDoc(doc(db, 'settings', 'studentServices'));
      if (servicesDoc.exists()) {
        this.cachedServices = servicesDoc.data().services;
        this.lastFetchTime = now;
        console.log('Student services refreshed from Firestore');
        return this.cachedServices;
      } else {
        // If not in Firestore, use the static data
        this.cachedServices = this.STUDENT_SERVICES;
        this.lastFetchTime = now;
        return this.STUDENT_SERVICES;
      }
    } catch (error) {
      console.error('Error fetching student services:', error);
      // In case of error, use cached values or default
      return this.cachedServices || this.STUDENT_SERVICES;
    }
  }

  // Generate a focused mock response
  static generateMockResponse(userMessage, relevantServices) {
    try {
      const lowercaseMessage = userMessage.toLowerCase();
      
      // Make sure we have the latest services data
      const servicesData = this.cachedServices || this.STUDENT_SERVICES;
      
      console.log('Generating mock response for:', userMessage);
      console.log('Relevant services:', relevantServices);
      
      // Check for greeting patterns
      if (lowercaseMessage.includes('hello') || 
          lowercaseMessage.includes('hi') || 
          lowercaseMessage.includes('hey')) {
        return "Hello! I'm your Student Helper Assistant. I can help you navigate our services including academic resources, career planning, campus information, technology support, and more. What are you looking for today?";
      } 
      
      // Check for general website questions
      if (lowercaseMessage.includes('how to use') ||
          lowercaseMessage.includes('website guide') ||
          lowercaseMessage.includes('website features') ||
          lowercaseMessage.includes('website help')) {
        return "Our Student Helper website offers many features to support your academic journey. You can access academic resources, career planning tools, campus information, technology support, manage your achievements, ask and answer questions in our Q&A section, share and download materials, update your profile, and communicate with others. Which feature would you like to learn more about?";
      }
      
      // Check for achievement-related questions
      if (lowercaseMessage.includes('achievement') ||
          lowercaseMessage.includes('point') ||
          lowercaseMessage.includes('contribution') ||
          lowercaseMessage.includes('ranking') ||
          lowercaseMessage.includes('leaderboard')) {
        return "In the Achievements section, you can track your contributions to the website. You earn points when you answer questions, ask quality questions, upload materials, and interact with the community. Your achievements are displayed as badges, and you can see your ranking on the leaderboard. The more you contribute, the higher your ranking will be. Visit the Achievements page by clicking on 'Achievements' in the navigation menu.";
      }
      
      // Check for Q&A related questions
      if (lowercaseMessage.includes('question') ||
          lowercaseMessage.includes('answer') ||
          lowercaseMessage.includes('qa') ||
          lowercaseMessage.includes('q&a')) {
        return "Our Q&A section allows you to ask questions about your coursework or academic needs. Other students and experts can provide answers, and you can upvote helpful responses. You can also answer questions to earn achievement points. Access the Q&A section by clicking on 'Q&A' in the main navigation menu.";
      }
      
      // Check for materials related questions
      if (lowercaseMessage.includes('material') ||
          lowercaseMessage.includes('resource') ||
          lowercaseMessage.includes('document') ||
          lowercaseMessage.includes('upload') ||
          lowercaseMessage.includes('download')) {
        return "The Materials section allows you to share and access study resources. You can upload your own materials to help others and download resources shared by the community. Materials are categorized by subject for easy searching. You'll earn achievement points when you contribute valuable resources. Access the Materials section by clicking on 'Materials' in the navigation menu.";
      }
      
      // Check for profile related questions
      if (lowercaseMessage.includes('profile') ||
          lowercaseMessage.includes('account') ||
          lowercaseMessage.includes('password') ||
          lowercaseMessage.includes('personal info')) {
        return "You can manage your profile by clicking on your username or the profile icon in the navigation menu. From there, you can update your personal information, change your password, view your activity history, and adjust your preferences. Your profile also shows your achievement points and badges.";
      }
      
      // Check for messaging related questions
      if (lowercaseMessage.includes('message') ||
          lowercaseMessage.includes('chat') ||
          lowercaseMessage.includes('communication') ||
          lowercaseMessage.includes('talk to')) {
        return "Our messaging system allows you to communicate directly with other users. You can send private messages, participate in group chats, and receive notifications when someone contacts you. Access your messages by clicking on the 'Messages' option in the navigation menu. You can also chat with our AI assistant (that's me!) or request to speak with a human admin for more complex inquiries.";
      }
      
      // Check for general help requests
      if (lowercaseMessage.includes('help') || 
          lowercaseMessage.includes('assist') || 
          lowercaseMessage.includes('support') ||
          lowercaseMessage.includes('what can you do')) {
        // Generate help message based on available services
        let helpMessage = "I can guide you to services on our website including: ";
        Object.entries(servicesData).forEach(([key, service], index, arr) => {
          helpMessage += `${service.description.toLowerCase()} (${service.features.slice(0, 2).join(', ')})`;
          if (index < arr.length - 1) {
            helpMessage += ", ";
          }
        });
        return helpMessage + ". What would you like help with?";
      }
      
      // Make sure relevantServices is valid - if not, use all services
      if (!relevantServices || !Array.isArray(relevantServices) || relevantServices.length === 0) {
        console.log('No relevant services found or invalid, using all services');
        relevantServices = Object.keys(servicesData);
      }
      
      // Generate responses based on the service content in the database
      for (const serviceKey of relevantServices) {
        if (servicesData[serviceKey]) {
          const service = servicesData[serviceKey];
          
          // Create specific responses for common queries within this service
          if (serviceKey === 'academics') {
            if (lowercaseMessage.includes('study') || lowercaseMessage.includes('exam') || lowercaseMessage.includes('tutor')) {
              return `Our ${serviceKey} section has ${service.features.join(', ')}. You can access these by clicking on '${serviceKey.charAt(0).toUpperCase() + serviceKey.slice(1)}' in the main menu. Would you like me to provide a direct link to a specific resource?`;
            }
          } else if (serviceKey === 'career') {
            if (lowercaseMessage.includes('resume') || lowercaseMessage.includes('cv')) {
              return `Our resume builder tool in the '${serviceKey.charAt(0).toUpperCase() + serviceKey.slice(1)}' section can help you create a professional resume. It includes templates and tips specific to different industries. Would you like to access the resume builder now?`;
            }
            if (lowercaseMessage.includes('job') || lowercaseMessage.includes('intern')) {
              return `You can find current job and internship listings in our '${serviceKey.charAt(0).toUpperCase() + serviceKey.slice(1)}' section. You can filter by field, location, and experience level. Would you like to browse the job board now?`;
            }
          }
          
          // Generic response for the service
          return `Our '${serviceKey.charAt(0).toUpperCase() + serviceKey.slice(1)}' section offers ${service.features.join(', ')}. What specific ${serviceKey} services are you interested in?`;
        }
      }
      
      // Default response if no specific service is identified
      return "I can help you navigate our Student Helper website. We offer features like academic resources, career planning, campus information, technology support, achievements tracking, Q&A forum, study materials, profile management, and messaging. What would you like to know more about?";
    } catch (error) {
      console.error('Error generating mock response:', error);
      // Provide a fallback response if there's any error in the mock generation logic
      return "I'm here to help with your questions about our Student Helper website. Would you like assistance with academic resources, Q&A, materials, achievements, or something else?";
    }
  }

  // Identify which services are relevant to the query
  static identifyRelevantServices(query) {
    const services = [];
    const lowercaseQuery = query.toLowerCase();
    const servicesData = this.cachedServices || this.STUDENT_SERVICES;
    
    // Iterate through all available services
    Object.entries(servicesData).forEach(([key, service]) => {
      // Check if query contains the service key
      if (lowercaseQuery.includes(key)) {
        services.push(key);
        return;
      }
      
      // Check if query contains any feature from this service
      for (const feature of service.features) {
        if (lowercaseQuery.includes(feature.toLowerCase())) {
          services.push(key);
          return;
        }
      }
      
      // Add additional common keywords for each service
      switch (key) {
        case 'academics':
          if (lowercaseQuery.includes('study') || 
              lowercaseQuery.includes('exam') || 
              lowercaseQuery.includes('course') || 
              lowercaseQuery.includes('class') ||
              lowercaseQuery.includes('tutor') ||
              lowercaseQuery.includes('academic') ||
              lowercaseQuery.includes('lecture') ||
              lowercaseQuery.includes('grade')) {
            services.push(key);
          }
          break;
        case 'career':
          if (lowercaseQuery.includes('job') || 
              lowercaseQuery.includes('career') || 
              lowercaseQuery.includes('resume') ||
              lowercaseQuery.includes('interview') ||
              lowercaseQuery.includes('internship') ||
              lowercaseQuery.includes('employment')) {
            services.push(key);
          }
          break;
        case 'campus':
          if (lowercaseQuery.includes('campus') || 
              lowercaseQuery.includes('event') || 
              lowercaseQuery.includes('housing') ||
              lowercaseQuery.includes('dorm') ||
              lowercaseQuery.includes('dining') ||
              lowercaseQuery.includes('food') ||
              lowercaseQuery.includes('library')) {
            services.push(key);
          }
          break;
        case 'technology':
          if (lowercaseQuery.includes('computer') || 
              lowercaseQuery.includes('software') || 
              lowercaseQuery.includes('tech') ||
              lowercaseQuery.includes('laptop') ||
              lowercaseQuery.includes('wifi') ||
              lowercaseQuery.includes('print')) {
            services.push(key);
          }
          break;
        case 'achievements':
          if (lowercaseQuery.includes('achievement') || 
              lowercaseQuery.includes('point') || 
              lowercaseQuery.includes('badge') ||
              lowercaseQuery.includes('trophy') ||
              lowercaseQuery.includes('reward') ||
              lowercaseQuery.includes('rank') ||
              lowercaseQuery.includes('level') ||
              lowercaseQuery.includes('contribution') ||
              lowercaseQuery.includes('leaderboard')) {
            services.push(key);
          }
          break;
        case 'qa':
          if (lowercaseQuery.includes('question') || 
              lowercaseQuery.includes('answer') || 
              lowercaseQuery.includes('q&a') ||
              lowercaseQuery.includes('qa') ||
              lowercaseQuery.includes('forum') ||
              lowercaseQuery.includes('ask') ||
              lowercaseQuery.includes('respond') ||
              lowercaseQuery.includes('solution')) {
            services.push(key);
          }
          break;
        case 'materials':
          if (lowercaseQuery.includes('material') || 
              lowercaseQuery.includes('resource') || 
              lowercaseQuery.includes('document') ||
              lowercaseQuery.includes('pdf') ||
              lowercaseQuery.includes('file') ||
              lowercaseQuery.includes('download') ||
              lowercaseQuery.includes('upload') ||
              lowercaseQuery.includes('share') ||
              lowercaseQuery.includes('content')) {
            services.push(key);
          }
          break;
        case 'profile':
          if (lowercaseQuery.includes('profile') || 
              lowercaseQuery.includes('account') || 
              lowercaseQuery.includes('password') ||
              lowercaseQuery.includes('setting') ||
              lowercaseQuery.includes('personal') ||
              lowercaseQuery.includes('detail') ||
              lowercaseQuery.includes('information') ||
              lowercaseQuery.includes('user info')) {
            services.push(key);
          }
          break;
        case 'messaging':
          if (lowercaseQuery.includes('message') || 
              lowercaseQuery.includes('chat') || 
              lowercaseQuery.includes('conversation') ||
              lowercaseQuery.includes('talk') ||
              lowercaseQuery.includes('communicate') ||
              lowercaseQuery.includes('contact') ||
              lowercaseQuery.includes('inbox') ||
              lowercaseQuery.includes('notification')) {
            services.push(key);
          }
          break;
        default:
          // For any custom services added through the admin interface
          // We already checked for service key and features above
          break;
      }
    });
    
    // If no specific services match, return all services for general guidance
    if (services.length === 0) {
      return Object.keys(servicesData);
    }
    
    return services;
  }

  // Save message to Firestore database
  static async saveMessageToFirestore(userId, message, isBotMessage) {
    try {
      const messagesRef = collection(db, 'users', userId, 'messages');
      await addDoc(messagesRef, {
        message,
        isUserMessage: !isBotMessage,
        timestamp: new Date().toISOString()
      });
      
      // Also save to global chat messages collection for admin analytics
      await addDoc(collection(db, 'chatMessages'), {
        userId,
        message,
        isUserMessage: !isBotMessage,
        timestamp: new Date()
      });
    } catch (error) {
      console.error('Error saving message to Firestore:', error);
    }
  }

  // Get user chat history from Firestore
  static async getUserChatHistory(userId, messageLimit = 20) {
    try {
      const messagesRef = collection(db, 'users', userId, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(messageLimit));
      const querySnapshot = await getDocs(q);
      
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      
      return messages.reverse();
    } catch (error) {
      console.error('Error getting chat history from Firestore:', error);
      return [];
    }
  }

  // Check if the user is requesting to speak with a human admin
  static isRequestingHumanAdmin(message) {
    const lowercaseMessage = message.toLowerCase();
    
    // Direct admin request patterns
    const adminRequestPatterns = [
      'speak to a human',
      'talk to a human',
      'speak to an admin',
      'talk to an admin',
      'speak with admin',
      'human support',
      'real person',
      'connect me with',
      'speak to a representative',
      'talk to a representative',
      'need human help',
      'chat with admin',
      'speak to someone',
      'talk to someone'
    ];
    
    // Service request patterns that should trigger admin involvement
    const serviceRequestPatterns = [
      'can you do projects',
      'project help',
      'need help with project',
      'project assistance',
      'tutoring',
      'tutor',
      'can you tutor',
      'private lessons',
      'one-on-one',
      'one on one',
      'personal help',
      'custom assignment',
      'assignment help',
      'help me with assignment',
      'help with homework',
      'homework assistance',
      'can someone help me with',
      'looking for someone to help',
      'need expert',
      'expert help',
      'paid service',
      'service cost',
      'how much for',
      'pricing',
      'payment'
    ];
    
    // Check for direct admin requests
    const isDirectRequest = adminRequestPatterns.some(pattern => lowercaseMessage.includes(pattern));
    
    // Check for service-related requests
    const isServiceRequest = serviceRequestPatterns.some(pattern => lowercaseMessage.includes(pattern));
    
    return isDirectRequest || isServiceRequest;
  }
  
  // Create an admin chat request in Firestore
  static async createAdminChatRequest(userId, message, context) {
    try {
      // First, check if there's already an active admin chat for this user
      const existingChats = await getDocs(
        query(
          collection(db, 'adminChats'),
          where('userId', '==', userId),
          where('status', 'in', ['pending', 'active'])
        )
      );
      
      if (!existingChats.empty) {
        // There's already an active chat, update it with the new message
        const existingChat = existingChats.docs[0];
        const chatRef = doc(db, 'adminChats', existingChat.id);
        
        await addDoc(collection(db, 'adminChats', existingChat.id, 'messages'), {
          content: message,
          timestamp: Timestamp.now(),
          senderId: userId,
          isAdmin: false
        });
        
        await updateDoc(chatRef, {
          lastMessage: message,
          lastMessageTime: Timestamp.now(),
          unreadCount: existingChat.data().unreadCount + 1
        });
        
        return existingChat.id;
      } else {
        // Create a new admin chat
        const userDoc = await getDoc(doc(db, 'users', userId));
        const userName = userDoc.exists() ? userDoc.data().displayName || userDoc.data().email : 'Anonymous User';
        
        const newChatRef = await addDoc(collection(db, 'adminChats'), {
          userId: userId,
          userName: userName,
          status: 'pending',
          createdAt: Timestamp.now(),
          lastMessage: message,
          lastMessageTime: Timestamp.now(),
          unreadCount: 1,
          assignedAdmin: null
        });
        
        // Add the initial message
        await addDoc(collection(db, 'adminChats', newChatRef.id, 'messages'), {
          content: message,
          timestamp: Timestamp.now(),
          senderId: userId,
          isAdmin: false,
          context: context.slice(-5).map(msg => ({ 
            text: msg.text || msg.content,
            isBot: msg.isBot !== undefined ? msg.isBot : (msg.role === 'assistant')
          }))
        });
        
        return newChatRef.id;
      }
    } catch (error) {
      console.error('Error creating admin chat request:', error);
      throw error;
    }
  }
  
  // Subscribe to admin chat updates (for users)
  static subscribeToAdminChat(userId, callback) {
    // First, find any active chats for this user
    this.findActiveChatForUser(userId).then(chatId => {
      if (!chatId) return;
      
      // If there's already a subscription for this chat, unsubscribe first
      if (this.chatSubscriptions[chatId]) {
        this.chatSubscriptions[chatId]();
      }
      
      // Subscribe to messages in this chat
      const messagesQuery = query(
        collection(db, 'adminChats', chatId, 'messages'),
        orderBy('timestamp', 'asc')
      );
      
      this.chatSubscriptions[chatId] = onSnapshot(messagesQuery, (snapshot) => {
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }));
        
        callback(messages, chatId);
      });
      
      // Also update the unread count to 0 for the user
      this.markChatAsRead(chatId, userId);
      
      return chatId;
    }).catch(error => {
      console.error('Error subscribing to admin chat:', error);
    });
  }
  
  // Find active chat for a user
  static async findActiveChatForUser(userId) {
    try {
      const chatsSnapshot = await getDocs(
        query(
          collection(db, 'adminChats'),
          where('userId', '==', userId),
          where('status', 'in', ['pending', 'active'])
        )
      );
      
      if (!chatsSnapshot.empty) {
        return chatsSnapshot.docs[0].id;
      }
      
      return null;
    } catch (error) {
      console.error('Error finding active chat:', error);
      return null;
    }
  }
  
  // Send a message in an admin chat
  static async sendAdminChatMessage(chatId, userId, message, isAdmin = false) {
    try {
      const chatRef = doc(db, 'adminChats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (!chatDoc.exists()) {
        throw new Error('Chat not found');
      }
      
      // Add the message to the messages subcollection
      await addDoc(collection(db, 'adminChats', chatId, 'messages'), {
        content: message,
        timestamp: Timestamp.now(),
        senderId: userId,
        isAdmin: isAdmin
      });
      
      // Update the chat document with the latest message
      await updateDoc(chatRef, {
        lastMessage: message,
        lastMessageTime: Timestamp.now(),
        unreadCount: chatDoc.data().unreadCount + 1,
        status: 'active'  // Ensure the chat is marked as active
      });
      
      return true;
    } catch (error) {
      console.error('Error sending admin chat message:', error);
      throw error;
    }
  }
  
  // Mark a chat as read
  static async markChatAsRead(chatId, userId) {
    try {
      const chatRef = doc(db, 'adminChats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (!chatDoc.exists()) {
        throw new Error('Chat not found');
      }
      
      // Only update if this is the user's chat
      if (chatDoc.data().userId === userId) {
        await updateDoc(chatRef, {
          unreadCount: 0
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error marking chat as read:', error);
      return false;
    }
  }
  
  // Get all admin chats (for admin dashboard)
  static async getAdminChats() {
    try {
      const chatsSnapshot = await getDocs(
        query(
          collection(db, 'adminChats'),
          where('status', 'in', ['pending', 'active']),
          orderBy('lastMessageTime', 'desc')
        )
      );
      
      return chatsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessageTime: doc.data().lastMessageTime?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting admin chats:', error);
      return [];
    }
  }
  
  // Assign an admin to a chat
  static async assignAdminToChat(chatId, adminId, adminName) {
    try {
      const chatRef = doc(db, 'adminChats', chatId);
      
      await updateDoc(chatRef, {
        assignedAdmin: adminId,
        adminName: adminName,
        status: 'active'
      });
      
      // Add a system message indicating the admin has joined
      await addDoc(collection(db, 'adminChats', chatId, 'messages'), {
        content: `Admin ${adminName} has joined the chat.`,
        timestamp: Timestamp.now(),
        senderId: 'system',
        isAdmin: true,
        isSystemMessage: true
      });
      
      return true;
    } catch (error) {
      console.error('Error assigning admin to chat:', error);
      return false;
    }
  }
  
  // Close an admin chat
  static async closeAdminChat(chatId, closedBy, closeMessage = 'This chat has been closed.') {
    try {
      const chatRef = doc(db, 'adminChats', chatId);
      
      await updateDoc(chatRef, {
        status: 'closed',
        closedAt: Timestamp.now(),
        closedBy: closedBy
      });
      
      // Add a system message indicating the chat has been closed
      await addDoc(collection(db, 'adminChats', chatId, 'messages'), {
        content: closeMessage,
        timestamp: Timestamp.now(),
        senderId: 'system',
        isAdmin: true,
        isSystemMessage: true
      });
      
      // Remove any active subscriptions
      if (this.chatSubscriptions[chatId]) {
        this.chatSubscriptions[chatId]();
        delete this.chatSubscriptions[chatId];
      }
      
      return true;
    } catch (error) {
      console.error('Error closing admin chat:', error);
      return false;
    }
  }

  // Subscribe to messages for a specific admin chat (for admin dashboard)
  static subscribeToAdminChatMessages(chatId, callback) {
    try {
      // Create a query for the messages subcollection of this chat
      const messagesQuery = query(
        collection(db, 'adminChats', chatId, 'messages'),
        orderBy('timestamp', 'asc')
      );
      
      // Set up the real-time listener
      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate()
        }));
        
        callback(messages);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error subscribing to admin chat messages:', error);
      return () => {}; // Return empty function as fallback
    }
  }

  // Helper to determine if a message is specifically requesting services
  static isServiceRequest(message) {
    const lowercaseMessage = message.toLowerCase();
    
    const serviceRequestPatterns = [
      'can you do projects',
      'project help',
      'need help with project',
      'project assistance',
      'tutoring',
      'tutor',
      'can you tutor',
      'private lessons',
      'one-on-one',
      'one on one',
      'personal help',
      'custom assignment',
      'assignment help',
      'help me with assignment',
      'help with homework',
      'homework assistance',
      'can someone help me with',
      'looking for someone to help',
      'need expert',
      'expert help',
      'paid service',
      'service cost',
      'how much for',
      'pricing',
      'payment'
    ];
    
    return serviceRequestPatterns.some(pattern => lowercaseMessage.includes(pattern));
  }

  // New debugging method to help troubleshoot issues
  static debug() {
    try {
      // Check initialization status
      const status = {
        initialized: this.isInitialized,
        useMockResponses: this.USE_MOCK_RESPONSES,
        hasApiKey: !!this.API_KEY,
        apiUrl: this.API_URL,
        cacheStatus: {
          hasCache: !!this.cachedServices,
          cacheAge: this.lastFetchTime ? (Date.now() - this.lastFetchTime) / 1000 + ' seconds' : 'never cached'
        },
        activeSubscriptions: Object.keys(this.chatSubscriptions).length
      };
      
      console.log('ChatGptService Debug Info:', status);
      
      // Test basic functionality
      const testMessage = "This is a test message.";
      const testService = this.identifyRelevantServices(testMessage);
      const mockWorks = !!this.generateMockResponse(testMessage, testService);
      
      console.log('Basic functionality test:', {
        mockResponseWorks: mockWorks,
        identifiedRelevantServices: testService
      });
      
      return {
        status,
        basicFunctionalityTest: {
          mockResponseWorks: mockWorks,
          identifiedRelevantServices: testService
        },
        troubleshootingSteps: [
          "1. Check if API_KEY is set in .env file: REACT_APP_OPENAI_API_KEY",
          "2. Verify Firebase configuration is correct",
          "3. If using mock responses, make sure they are properly implemented",
          "4. Check browser console for any errors related to API calls",
          "5. Verify network connectivity and CORS settings if applicable"
        ],
        fixSuggestion: "To use mock responses while troubleshooting, make sure USE_MOCK_RESPONSES is set to true"
      };
    } catch (error) {
      console.error('Error in debug method:', error);
      return {
        error: 'Failed to debug ChatGptService',
        errorMessage: error.message,
        troubleshootingSteps: [
          "1. Ensure all dependencies are properly installed",
          "2. Check for syntax errors in the ChatGptService code",
          "3. Verify that the firebase.js file is properly configured"
        ]
      };
    }
  }

  // A direct test method to check if basic messaging works
  static async testChat(testMessage = "Hello, is this working?") {
    try {
      this.initialize();
      console.log("Running chat test with: " + testMessage);
      
      // For testing, always use mock responses
      const originalUseMock = this.USE_MOCK_RESPONSES;
      this.USE_MOCK_RESPONSES = true;
      
      const response = await this.getResponse(testMessage, "test-user", []);
      
      // Restore original setting
      this.USE_MOCK_RESPONSES = originalUseMock;
      
      return {
        success: true,
        testMessage,
        response,
        mockMode: true
      };
    } catch (error) {
      console.error("Chat test failed:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add this method at the end of the class, right before the final closing brace
  static directChat(userMessage) {
    console.log('Using directChat with message:', userMessage);
    
    // Simple patterns and responses that don't need any service dependencies
    const patterns = [
      { 
        match: (msg) => /hello|hi|hey|greetings/i.test(msg), 
        response: "Hello! I'm your Student Helper Assistant. How can I help you today?"
      },
      { 
        match: (msg) => /help|assist|support/i.test(msg), 
        response: "I can help you with academic resources, career planning, campus info, achievements, Q&A, materials, profiles, and messaging. What do you need help with?"
      },
      { 
        match: (msg) => /achievement|point|rank|leaderboard/i.test(msg), 
        response: "In the Achievements section, you can track your contributions, points, badges, and ranking on the leaderboard. Visit the Achievements page in the navigation menu to see your status."
      },
      { 
        match: (msg) => /question|answer|qa|q&a/i.test(msg), 
        response: "Our Q&A section lets you ask questions and get answers from other students. You earn points by providing helpful answers."
      },
      { 
        match: (msg) => /material|resource|document|upload|download/i.test(msg), 
        response: "The Materials section allows you to share and download study resources. Materials are categorized by subject for easy searching."
      },
      { 
        match: (msg) => /profile|account|password/i.test(msg), 
        response: "You can manage your profile by clicking your username in the navigation menu. Update your info, change your password, and view your activity there."
      },
      { 
        match: (msg) => /message|chat|communication/i.test(msg), 
        response: "You can communicate with other users through our messaging system. Access your messages from the navigation menu."
      }
    ];
    
    // Find a matching pattern
    for (const pattern of patterns) {
      if (pattern.match(userMessage)) {
        return pattern.response;
      }
    }
    
    // Default response
    return "I can help you navigate our Student Helper website. We offer features like academic resources, career planning, campus information, technology support, achievements tracking, Q&A forum, study materials, profile management, and messaging. What would you like to know more about?";
  }
}

export default ChatGptService; 